import React, { FC, useMemo } from 'react'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { TableCell, TableRow } from '@mui/material'
import {
  Banner,
  DataTable,
  type DataTableColumn,
  KpiTile,
  PageLoading,
  PageScaffold,
  SubsectionHeader,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import { MetaText } from '@/components/atoms/MetaText'
import {
  useKasanAnalysisReportQuery,
  KasanAnalysisItem,
  KasanAnalysisMonthly,
} from '@/features/internal/kasanAnalysis'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'

// イ/ロは実装用語でなく制度上の区分名。初出時のtooltipで1行定義する
const VARIANT_DEFINITIONS: Record<'i' | 'ro', string> = {
  i: 'イ: 対象薬が新たに処方された場合 (10点)',
  ro: 'ロ: 用法・用量の変更など状況の変化があった場合 (5点)',
}

const RULE_LABELS: Record<'i' | 'ro', string> = {
  i: 'イ',
  ro: 'ロ',
}

// KPI・チャートの「算定機会」の定義 (一覧テーブルの検知/レビュー2列と整合)
const OPPORTUNITY_DEFINITION =
  '算定機会 = ベースモデル検知のうち、レビューで棄却されなかった件数'

// レビュー結果 (LLMブラインド独立判定) は加算欄の○×文法の拡張マークで示す:
//   ○=機会と確定 / ×=非該当(検知側の誤り=ロジック修正のバックログ) / △=低確信・要目視
// 理由はマークのhoverで表示。未レビューは全量run反映までの過渡状態
const REVIEW_MARKS: Record<
  NonNullable<KasanAnalysisItem['review']>,
  { mark: string; label: string; color: string }
> = {
  i: { mark: '○', label: 'イで確定', color: colorTokens.fg.default },
  ro: { mark: '○', label: 'ロで確定', color: colorTokens.fg.default },
  rejected: { mark: '×', label: '非該当', color: colorTokens.fg.faint },
  uncertain: { mark: '△', label: '要目視', color: colorTokens.fg.muted },
}

const yen = (value: number): string => `${value.toLocaleString()}円`

const dateLabel = (date: string): string => date.split('-').join('/')

// 2024-06改定でイ/ロが新設された。チャートは改定後のみを表示する
// (旧コード時代の実算定はスケールが2桁違い、肝心の「機会」を潰してしまう)
const REFORM_MONTH = '2024-06'

export const KasanAnalysisPage: FC = () => {
  const { report, isLoading, error } = useKasanAnalysisReportQuery()

  const payload = report?.payload ?? null

  // ヘッドライン = 機会が確定している直近月
  const latestMonth: KasanAnalysisMonthly | null = useMemo(() => {
    if (!payload) return null
    // payloadの配列順に依存せず月で並べ替えてから末尾を採る
    const withOpportunity = payload.monthly
      .filter((m) => m.opportunity_i + m.opportunity_ro > 0)
      .sort((a, b) => a.month.localeCompare(b.month))
    return withOpportunity[withOpportunity.length - 1] ?? null
  }, [payload])

  const chartData = useMemo(() => {
    if (!payload) return []
    return payload.monthly
      .filter((m) => m.month >= REFORM_MONTH)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map((m) => ({
        月: m.month,
        実算定: m.actual_i + m.actual_ro,
        算定機会: m.opportunity_i + m.opportunity_ro,
      }))
  }, [payload])

  const items = payload?.items ?? []

  const itemColumns: DataTableColumn[] = [
    { key: 'date', label: '日付' },
    { key: 'patient', label: '患者名' },
    { key: 'medication', label: '代表処方' },
    { key: 'rule', label: 'ベースモデル検知', align: 'center' },
    { key: 'review', label: 'レビュー結果', align: 'center' },
  ]

  return (
    <PageScaffold
      title="算定分析"
      // 算定機会一覧も列の多い正直開示テーブルなので統計ページと同じ扱いにする
      wide
    >
      {isLoading && <PageLoading />}
      {error && <Banner severity="danger">レポートの取得に失敗しました</Banner>}

      {!isLoading && !error && !payload && (
        <Typography sx={PORTAL_TEXT.empty}>(準備中)</Typography>
      )}

      {payload && (
        <Stack gap={4}>
          <Box>
            <SubsectionHeader heading="算定機会の推移" />
            {latestMonth && (
              <Grid container columnSpacing={0} rowSpacing={1}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Tooltip title={OPPORTUNITY_DEFINITION} placement="top">
                    <Box>
                      <KpiTile
                        title={`${latestMonth.month} の算定機会`}
                        value={
                          latestMonth.opportunity_i + latestMonth.opportunity_ro
                        }
                        unit="件"
                      />
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Tooltip title={VARIANT_DEFINITIONS.i} placement="top">
                    <Box>
                      <KpiTile
                        title="内訳: イ"
                        value={latestMonth.opportunity_i}
                        unit="件"
                        divider
                      />
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Tooltip title={VARIANT_DEFINITIONS.ro} placement="top">
                    <Box>
                      <KpiTile
                        title="内訳: ロ"
                        value={latestMonth.opportunity_ro}
                        unit="件"
                        divider
                      />
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <KpiTile
                    title="金額換算"
                    value={yen(latestMonth.amount_yen)}
                    divider
                  />
                </Grid>
              </Grid>
            )}
            <Box sx={{ width: '100%', height: 280, mt: 2 }}>
              <ResponsiveContainer>
                <ComposedChart data={chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={colorTokens.border.faint}
                  />
                  <XAxis
                    dataKey="月"
                    tick={{ fontSize: 11, fill: colorTokens.fg.muted }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: colorTokens.fg.muted }}
                    unit="件"
                  />
                  <ChartTooltip />
                  {/* 実算定=塗り・緑 (取った=完了) / 算定機会=輪郭・グレー (まだ取っていない)。
                      StatusDotの塗り/輪郭二重符号化の転用。損失を赤で塗らない。
                      凡例は自前描画 (rechartsのLegendは輪郭系列のswatchが透明になり見えない) */}
                  <Bar
                    dataKey="実算定"
                    fill={colorTokens.state.successFg}
                    stackId="a"
                  />
                  <Bar
                    dataKey="算定機会"
                    fill="transparent"
                    stroke={colorTokens.fg.muted}
                    strokeWidth={1}
                    stackId="a"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
            <Stack
              direction="row"
              justifyContent="center"
              gap={3}
              sx={{ mt: 1 }}
            >
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    bgcolor: colorTokens.state.successFg,
                  }}
                />
                <MetaText>実算定</MetaText>
              </Stack>
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    border: `1px solid ${colorTokens.fg.muted}`,
                  }}
                />
                <MetaText>算定機会 (未算定)</MetaText>
              </Stack>
            </Stack>
          </Box>

          {items.length > 0 && (
            <Box>
              <SubsectionHeader
                heading="算定機会の一覧"
                description="ベースモデル検知は、来局のたびに服薬指導画面の「加算」欄に自動で表示されます"
              />
              <Box sx={{ overflowX: 'auto' }}>
                <DataTable columns={itemColumns}>
                  {items.map((item, index) => (
                    <TableRow key={`${item.date}-${index}`}>
                      <TableCell>{dateLabel(item.date)}</TableCell>
                      <TableCell>{item.patient_name}</TableCell>
                      <TableCell>{item.medication}</TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={VARIANT_DEFINITIONS[item.rule]}
                          placement="top"
                        >
                          <span>{RULE_LABELS[item.rule]}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          // payloadはバッチ生成JSONのため、未知のレビュー値が来ても
                          // ページ全体を落とさない (未レビュー表示に倒す)
                          const mark = item.review
                            ? REVIEW_MARKS[item.review]
                            : undefined
                          if (!mark) return <MetaText>未レビュー</MetaText>
                          return (
                            <Tooltip
                              title={`${mark.label}${
                                item.reason ? `: ${item.reason}` : ''
                              }`}
                              placement="top"
                            >
                              <Box
                                component="span"
                                sx={{
                                  color: mark.color,
                                  cursor: 'default',
                                }}
                              >
                                {mark.mark}
                              </Box>
                            </Tooltip>
                          )
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                </DataTable>
              </Box>
              {payload?.over_detection_rate != null && (
                <MetaText sx={{ mt: 1 }}>
                  {`レビュー済 ${payload.judged_count.toLocaleString()} 件のうち、検知が誤りだったもの ${(
                    payload.over_detection_rate * 100
                  ).toFixed(1)}%`}
                </MetaText>
              )}
            </Box>
          )}
        </Stack>
      )}
    </PageScaffold>
  )
}
